/** GMA-1402: client profile visibility (FE mirrors BE ACL). */

export function isCaregiverAdminOrSuper(user) {
  return Boolean(user?.isSuperAdmin || user?.isAdmin);
}

/** Pontosense staff pane on client profile.
 *  caregiver_admin / super_admin: always visible (they own bind/unbind, and need
 *    the pane precisely when there is no sensor yet — otherwise the first one
 *    could never be bound).
 *  Standard caregivers: only when the client actually HAS a sensor, and the
 *    integration is explicitly enabled (default OFF — cortex-be#1501 / GMA-1402).
 *
 *  `hasDevice` is what establishes PRESENCE, and is required for standard
 *  caregivers; `integrationEnabled` must also be exactly `true`. Presence is the
 *  half `integrationEnabled` cannot answer: the flag answers "is this
 *  permitted", never "does this client have one" (clients created before
 *  cortex-be#1501 still carry the old default of true) — gating on it rendered an empty pane for every client that
 *  never had a sensor. The device fact comes from the profile payload's
 *  `iotVendors`, which counts inactive rows too, because unbinding is soft and a
 *  device swap must not blank the pane mid-care.
 *
 *  `hasDevice` defaults to FALSE: a payload served before the backend carried
 *  the field has no device fact, and hiding a pane that should show is a refresh
 *  away, where showing an empty one is the defect this exists to fix.
 *
 *  Per-client assignment is enforced by BE when loading the profile — not re-checked here. */
export function canShowPontosenseStaffPane(user, options) {
  const { integrationEnabled = false, hasDevice = false } = options ?? {};
  const isAdmin = isCaregiverAdminOrSuper(user);
  if (!user?.isCaregiver && !isAdmin) {
    return false;
  }
  if (isAdmin) {
    return true;
  }
  return hasDevice === true && integrationEnabled === true;
}

/** Xandar Kardian staff pane on client profile (GMA-786, mirrors GMA-1402).
 *  caregiver_admin / super_admin: always visible (toggle always; portal link
 *  only when integration is enabled — same as Pontosense bind UI gating).
 *  Standard caregivers: only when xandarKardianEnabled === true (opt-in). */
export function canShowXandarStaffPane(user, options) {
  const { integrationEnabled = false } = options ?? {};
  const isAdmin = isCaregiverAdminOrSuper(user);
  if (!user?.isCaregiver && !isAdmin) {
    return false;
  }
  if (isAdmin) {
    return true;
  }
  return integrationEnabled === true;
}

/** Apple Watch vitals pane on client profile (GMA-1571, mirrors Xandar GMA-786).
 *  caregiver_admin / super_admin: always visible (manages the on/off toggle).
 *  Standard caregivers: only when appleWatchEnabled === true — once an admin
 *  turns the Watch on, the live-vitals pane appears for the caregiver too. */
export function canShowWatchStaffPane(user, options) {
  const { integrationEnabled = false } = options ?? {};
  const isAdmin = isCaregiverAdminOrSuper(user);
  if (!user?.isCaregiver && !isAdmin) {
    return false;
  }
  if (isAdmin) {
    return true;
  }
  return integrationEnabled === true;
}

/** My Compass password reset: caregiver_admin / super_admin only. */
export function canResetClientPassword(user) {
  return isCaregiverAdminOrSuper(user);
}

/**
 * Per-device build chips on the client profile (#529 defect 3, narrowing #489).
 *
 * The row is a QA/admin diagnostic — "is this client on build 108 or 119?" — and
 * it deliberately renders one chip per DEVICE IDENTITY rather than per device,
 * so a reinstall or a device migration leaves a second chip for the same
 * hardware. Those stale identities persist until cortex-be prunes the node
 * (`service/presenceStaleness.js` DEFAULT_PRESENCE_DEVICE_PRUNE_AGE_MS, 30d), so
 * a caregiver opening a profile could be shown five near-identical chips, two of
 * them weeks dead, in the most valuable space on the page — while the question
 * they actually have is "is this client's device working right now?".
 *
 * Restricting to caregiver_admin / super_admin keeps #489's answer where it is
 * needed and takes it off the caregiver's primary surface. It does NOT settle
 * the retention/disclosure question for the admin view — that is still open on
 * #529, joined to #323.
 *
 * Strict `=== true` checks, mirroring canEditClientDetails (GMA-1864): an
 * absent user, a role-less user, AND truthy junk in JWT claims (`isAdmin:
 * "false"`, `isAdmin: 1`) all fail CLOSED. isCaregiverAdminOrSuper's
 * `Boolean(a || b)` is deliberately not reused here — a truthy string opens it
 * (Copilot review on #585).
 */
export function canShowDeviceBuildChips(user) {
  if (!user || typeof user !== "object") {
    return false;
  }
  return user.isSuperAdmin === true || user.isAdmin === true;
}

/**
 * Client Basic info / details edit (GMA-1864).
 * caregiver_admin / super_admin only — standard caregivers view read-only.
 * BE `PUT /users/:id` still requires admin; FE must not show Edit for caregivers.
 * Strict true checks so truthy junk in JWT claims cannot open the edit flow.
 */
export function canEditClientDetails(user) {
  if (!user || typeof user !== "object") {
    return false;
  }
  return user.isSuperAdmin === true || user.isAdmin === true;
}

/** Home-page Clients' Map (#747).
 *  Plain caregivers only, for now: super_admin and caregiver_admin land on the
 *  Assignment Center on the same page and the map is not for them yet. A user
 *  carrying an admin or contact claim alongside the caregiver claim is not a
 *  plain caregiver here. Strict true on the caregiver claim, so truthy junk
 *  cannot open the map. */
export function canShowHomeClientsMap(user) {
  if (!user || typeof user !== "object") {
    return false;
  }
  // Inverse gate: every non-caregiver claim (admin, super_admin, contact) must
  // be ABSENT, null or exactly false. A malformed value ("true", 1, {}) is not
  // "not set" — it fails closed. A user carrying caregiver AND contact claims
  // is ambiguous, and ambiguity fails closed too.
  const unset = (claim) => claim === undefined || claim === null || claim === false;
  if (!unset(user.isSuperAdmin) || !unset(user.isAdmin) || !unset(user.isContact)) {
    return false;
  }
  return user.isCaregiver === true;
}
